// ─────────────────────────────────────────────────────────────────────────────
// WidgetRegistry – Singleton store for registered dashboard widgets
//
// Why a singleton (and not Redux):
// - Keeps widget data out of Redux DevTools time-travel buffer (PII surface).
// - No serialization in the time-travel snapshot.
// - Pub/sub model lets the AI assistant subscribe via useSyncExternalStore
//   without coupling to the host app's reducers.
//
// Lifecycle:
// - Renderers call window.__esgAssistant.register(...) on mount/data-update,
//   .update(...) when filters change, .unregister(...) on unmount.
// - Stale entries (no update within staleAfterMs) are evicted on next list().
// ─────────────────────────────────────────────────────────────────────────────

import type {
	RegisteredWidget,
	RegistryEvent,
	RegistrySubscriber,
	WidgetIndexEntry,
	WidgetRegistryAPI,
} from "./types";

const DEFAULT_STALE_MS = 5 * 60_000;

const widgets = new Map<string, RegisteredWidget>();
const subscribers = new Set<RegistrySubscriber>();
let staleAfterMs = DEFAULT_STALE_MS;

function notify(event: RegistryEvent): void {
	subscribers.forEach((fn) => {
		try {
			fn(event);
		} catch {
			/* never let a bad subscriber take down the chain */
		}
	});
}

function evictStale(): number {
	const now = Date.now();
	let evicted = 0;
	for (const [id, w] of widgets) {
		if (now - w.updatedAt > staleAfterMs) {
			widgets.delete(id);
			evicted++;
			notify({ type: "remove", widgetId: id, dashboardId: w.dashboardId });
		}
	}
	return evicted;
}

function indexEntry(w: RegisteredWidget): WidgetIndexEntry {
	return {
		widgetId: w.widgetId,
		title: w.title,
		kind: w.kind,
		chartLibrary: w.chartLibrary,
		dashboardId: w.dashboardId,
		framework: w.framework,
		period: w.period,
		units: w.units,
		seriesCount: w.series?.length,
		rowCount: w.table?.totalRows ?? w.table?.rows.length,
		updatedAt: w.updatedAt,
	};
}

const api: WidgetRegistryAPI = {
	get staleAfterMs() {
		return staleAfterMs;
	},
	set staleAfterMs(ms: number) {
		staleAfterMs = Math.max(60_000, ms);
	},

	register(w) {
		const now = Date.now();
		const existing = widgets.get(w.widgetId);
		const entry: RegisteredWidget = {
			...w,
			registeredAt: existing?.registeredAt ?? now,
			updatedAt: now,
		};
		widgets.set(w.widgetId, entry);
		notify({
			type: existing ? "update" : "add",
			widgetId: w.widgetId,
			dashboardId: w.dashboardId,
		});
	},

	update(widgetId, patch) {
		const existing = widgets.get(widgetId);
		if (!existing) return;
		const next: RegisteredWidget = {
			...existing,
			...patch,
			widgetId: existing.widgetId, // identity is immutable
			registeredAt: existing.registeredAt,
			updatedAt: Date.now(),
		};
		widgets.set(widgetId, next);
		notify({ type: "update", widgetId, dashboardId: existing.dashboardId });
	},

	unregister(widgetId) {
		const existing = widgets.get(widgetId);
		if (!existing) return;
		widgets.delete(widgetId);
		notify({ type: "remove", widgetId, dashboardId: existing.dashboardId });
	},

	list() {
		evictStale();
		return Array.from(widgets.values());
	},

	get(widgetId) {
		evictStale();
		return widgets.get(widgetId);
	},

	getIndex() {
		evictStale();
		return Array.from(widgets.values()).map(indexEntry);
	},

	subscribe(fn) {
		subscribers.add(fn);
		return () => {
			subscribers.delete(fn);
		};
	},

	clearDashboard(dashboardId) {
		const ids: string[] = [];
		for (const [id, w] of widgets) {
			if (w.dashboardId === dashboardId) ids.push(id);
		}
		ids.forEach((id) => widgets.delete(id));
		notify({ type: "clear", dashboardId });
	},

	clearAll() {
		widgets.clear();
		notify({ type: "clear" });
	},
};

if (typeof window !== "undefined" && !window.__esgAssistant) {
	window.__esgAssistant = api;
}

export default api;
export const widgetRegistry = api;
